// src/components/TaskDashboard.js
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import TaskTable from './TaskTable';
import TaskChart from './TaskChart';
import TaskUploadForm from './TaskUploadForm';
import TaskForm from './TaskForm';
import { resetTaskState, fetchTasks } from '../features/tasks/taskSlice';

const TaskDashboard = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('table');
  const [inputMethod, setInputMethod] = useState('upload');
  const { tasks, isLoading, isError, message } = useSelector(state => state.tasks);

  useEffect(() => {
    console.log('🚀 TaskDashboard mounted, fetching tasks...');
    // Reset task state when component mounts
    dispatch(resetTaskState());
    // Fetch existing tasks from the database
    dispatch(fetchTasks());
  }, [dispatch]);

  // Add debugging for task state changes
  useEffect(() => {
    console.log('=== TASK DASHBOARD DEBUG ===');
    console.log('Tasks from Redux:', tasks);
    console.log('Tasks length:', tasks?.length);
    console.log('Loading state:', isLoading);
    console.log('Error state:', isError);
    console.log('Error message:', message);
    console.log('===========================');
  }, [tasks, isLoading, isError, message]);

  return (
    <div className="task-dashboard">
      <div className="dashboard-header">
        <h1>Task Prioritization Dashboard</h1>
        <p>Upload tasks or enter them manually to get AI-powered priority analysis</p>
      </div>

      <div className="input-section">
        <div className="input-toggle">
          <button
            className={inputMethod === 'upload' ? 'active' : ''}
            onClick={() => setInputMethod('upload')}
          >
            Upload CSV
          </button>
          <button
            className={inputMethod === 'manual' ? 'active' : ''}
            onClick={() => setInputMethod('manual')}
          >
            Enter Manually
          </button>
        </div>

        {inputMethod === 'upload' ? <TaskUploadForm /> : <TaskForm />}
      </div>

      {isError && (
        <div className="error-message">
          <p>Error: {message}</p>
          <p>Make sure your backend server is running on http://localhost:8000</p>
        </div>
      )}

      {tasks.length > 0 && (
        <div className="results-section">
          <div className="tabs">
            <button
              className={activeTab === 'table' ? 'active' : ''}
              onClick={() => setActiveTab('table')}
            >
              Table View
            </button>
            <button
              className={activeTab === 'chart' ? 'active' : ''}
              onClick={() => setActiveTab('chart')}
            >
              Chart View
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'table' ? (
              <TaskTable tasks={tasks} />
            ) : (
              <TaskChart tasks={tasks} />
            )}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading tasks...</p>
        </div>
      )}

      {!isLoading && tasks.length === 0 && !isError && (
        <div className="empty-state">
          <h3>No tasks found</h3>
          <p>Upload a CSV file or enter tasks manually to get started with AI-powered prioritization.</p>
        </div>
      )}

      {/* Your existing styles */}
      <style jsx>{`
            
            /* TaskDashboard Styles */
.task-dashboard {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: calc(100vh - var(--header-height, 60px) - var(--footer-height, 120px));
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

/* Dashboard Header */
.dashboard-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 30px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  color: white;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.dashboard-header::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
  50% { transform: translate(-50%, -50%) rotate(180deg); }
}

.dashboard-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 15px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
}

.dashboard-header p {
  font-size: 1.2rem;
  opacity: 0.9;
  font-weight: 300;
  position: relative;
  z-index: 1;
}

/* Input Section */
.input-section {
  background: white;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.input-toggle {
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 6px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 30px;
}

.input-toggle button {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: transparent;
  color: #6c757d;
  position: relative;
  overflow: hidden;
}

.input-toggle button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.5s;
}

.input-toggle button:hover::before {
  left: 100%;
}

.input-toggle button.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transform: translateY(-2px);
}

.input-toggle button:hover:not(.active) {
  background: #e9ecef;
  transform: translateY(-1px);
}

/* Results Section */
.results-section {
  background: white;
  border-radius: 16px;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.tabs {
  display: flex;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  padding: 0;
}

.tabs button {
  flex: 1;
  padding: 20px 30px;
  border: none;
  background: transparent;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #6c757d;
  position: relative;
  border-bottom: 3px solid transparent;
}

.tabs button::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 3px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tabs button.active {
  color: #667eea;
  background: white;
}

.tabs button.active::after {
  width: 100%;
}

.tabs button:hover:not(.active) {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
}

.tab-content {
  padding: 30px;
  min-height: 400px;
}

/* Error Message */
.error-message {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(255, 107, 107, 0.3);
  border-left: 5px solid #ff5252;
}

.error-message p {
  margin: 0;
  font-weight: 500;
}

.error-message p:first-child {
  font-size: 18px;
  margin-bottom: 8px;
}

.error-message p:last-child {
  font-size: 14px;
  opacity: 0.9;
}

/* Loading State */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 16px;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading p {
  font-size: 18px;
  color: #667eea;
  font-weight: 600;
  margin: 0;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 16px;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 2px dashed #dee2e6;
  position: relative;
  overflow: hidden;
}

.empty-state::before {
  content: '📊';
  font-size: 80px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.1;
  z-index: 1;
}

.empty-state h3 {
  font-size: 24px;
  color: #495057;
  margin-bottom: 15px;
  position: relative;
  z-index: 2;
}

.empty-state p {
  font-size: 16px;
  color: #6c757d;
  margin: 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 2;
}

/* Form Styles (for TaskUploadForm and TaskForm) */
.task-upload-form,
.task-form {
  padding: 0;
}

/* Task Form Specific Styles */
.task-form .form-group {
  margin-bottom: 25px;
}

.task-form .form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.task-form .form-group input,
.task-form .form-group textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.task-form .form-group input:focus,
.task-form .form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1), 0 2px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.task-form .form-group textarea {
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
}

.task-form .form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
}

/* Task Input Container */
.task-input {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.task-input::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.05), transparent);
  transition: left 0.6s;
}

.task-input:hover::before {
  left: 100%;
}

.task-input:hover {
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
}

.task-input h3 {
  margin: 0 0 20px 0;
  color: #495057;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 2;
}

.task-input h3::before {
  content: "📋";
  font-size: 20px;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  margin-top: 30px;
  padding-top: 25px;
  border-top: 2px solid #e9ecef;
}

/* Add Another Task Button */
.form-actions button[type="button"] {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
}

.form-actions button[type="button"]::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s;
}

.form-actions button[type="button"]:hover::before {
  left: 100%;
}

.form-actions button[type="button"]:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
  background: linear-gradient(135deg, #218838 0%, #1dc79a 100%);
}

.form-actions button[type="button"]:active {
  transform: translateY(0);
  box-shadow: 0 2px 10px rgba(40, 167, 69, 0.3);
}

/* Analyze Tasks Button */
.form-actions button[type="submit"] {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  min-width: 160px;
}

.form-actions button[type="submit"]::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s;
}

.form-actions button[type="submit"]:hover::before {
  left: 100%;
}

.form-actions button[type="submit"]:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
  background: linear-gradient(135deg, #5a6fd8 0%, #6c4f9c 100%);
}

.form-actions button[type="submit"]:active {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.form-actions button[type="submit"]:disabled {
  background: linear-gradient(135deg, #6c757d 0%, #868e96 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
}

.form-actions button[type="submit"]:disabled:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
}

/* Button Loading State */
.form-actions button[type="submit"]:disabled::after {
  content: '';
  width: 16px;
  height: 16px;
  margin-left: 10px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
}

/* Primary Button (for Upload & Analyze) */
.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  min-width: 140px;
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s;
}

.btn-primary:hover::before {
  left: 100%;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #5a6fd8 0%, #6c4f9c 100%);
}

.btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.btn-primary:disabled {
  background: linear-gradient(135deg, #6c757d 0%, #868e96 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
}

.btn-primary:disabled:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
}

/* File Upload Area */
.file-upload-area {
  border: 3px dashed #dee2e6;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #f8f9fa;
  position: relative;
  overflow: hidden;
}

.file-upload-area::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
  transition: left 0.6s;
}

.file-upload-area:hover::before {
  left: 100%;
}

.file-upload-area:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
  transform: translateY(-2px);
}

.file-upload-area.drag-active {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  transform: scale(1.02);
}

.file-upload-label {
  cursor: pointer;
  display: block;
  position: relative;
  z-index: 2;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 20px;
  opacity: 0.7;
}

.upload-content p {
  margin: 5px 0;
  color: #495057;
}

.upload-content p:first-of-type {
  font-weight: 600;
  font-size: 18px;
}

.upload-content p:last-of-type {
  font-size: 14px;
  color: #6c757d;
}

.form-help {
  margin: 20px 0;
  padding: 15px;
  background: #e3f2fd;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.form-help p {
  margin: 0 0 10px 0;
  color: #1565c0;
  font-size: 14px;
}

.btn-link {
  background: none;
  border: none;
  color: #667eea;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  transition: color 0.3s;
}

.btn-link:hover {
  color: #764ba2;
}

/* Error State for Form Fields */
.form-group input.error,
.form-group textarea.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
  font-weight: 500;
}

/* Success State for Form Fields */
.form-group input:valid:not(:placeholder-shown),
.form-group textarea:valid:not(:placeholder-shown) {
  border-color: #28a745;
}

/* Responsive Adjustments for Form Actions */
@media (max-width: 768px) {
  .form-actions {
    flex-direction: column;
    gap: 15px;
  }
  
  .form-actions button {
    width: 100%;
    padding: 16px 24px;
    font-size: 16px;
  }

  .task-form .form-row {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .task-input {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .form-actions button {
    padding: 18px 24px;
    font-size: 14px;
  }

  .task-input {
    padding: 15px;
  }

  .task-input h3 {
    font-size: 16px;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .task-dashboard {
    padding: 15px;
    margin: 10px;
  }

  .dashboard-header {
    padding: 20px 15px;
    margin-bottom: 20px;
  }

  .dashboard-header h1 {
    font-size: 2rem;
  }

  .dashboard-header p {
    font-size: 1rem;
  }

  .input-section {
    padding: 20px;
    margin-bottom: 20px;
  }

  .input-toggle {
    flex-direction: column;
    max-width: none;
  }

  .input-toggle button {
    margin-bottom: 5px;
  }

  .tabs {
    flex-direction: column;
  }

  .tabs button {
    border-bottom: 1px solid #dee2e6;
    border-right: none;
  }

  .tabs button.active::after {
    width: 0;
    height: 0;
  }

  .tabs button.active {
    border-left: 4px solid #667eea;
  }

  .tab-content {
    padding: 20px;
  }

  .file-upload-area {
    padding: 30px 15px;
  }

  .upload-icon {
    font-size: 36px;
  }

  .empty-state {
    padding: 40px 20px;
  }

  .empty-state::before {
    font-size: 60px;
  }
}

@media (max-width: 480px) {
  .dashboard-header h1 {
    font-size: 1.5rem;
  }

  .dashboard-header p {
    font-size: 0.9rem;
  }

  .input-section,
  .results-section {
    margin-bottom: 15px;
  }

  .file-upload-area {
    padding: 20px 10px;
  }

  .upload-content p:first-of-type {
    font-size: 16px;
  }
}

/* Success State Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.results-section {
  animation: slideInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover Effects for Better UX */
.input-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.results-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

/* Focus States for Accessibility */
.input-toggle button:focus,
.tabs button:focus {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}

/* Print Styles */
@media print {
  .task-dashboard {
    background: white;
    box-shadow: none;
    padding: 20px;
  }

  .dashboard-header {
    background: #f8f9fa;
    color: #333;
  }

  .input-section {
    display: none;
  }

  .loading,
  .empty-state {
    display: none;
  }
}
            `}</style>
    </div>
  );
};

export default TaskDashboard;